/**
 * Drag-and-drop or click-to-upload control for a single image with size/type validation.
 *
 * Where: `Profile` (avatar) and any settings form that needs binary image selection.
 * Uses: MUI `Paper`/`IconButton`; local drag state and validation only (upload is delegated via `onFileSelect`).
 * Behavior: enforces `accept` and `maxSize`; shows inline error; optional loading and preview URL.
 */
import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onFileRemove?: () => void;
  currentImageUrl?: string;
  isLoading?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  title?: string;
  description?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onFileRemove,
  currentImageUrl,
  isLoading = false,
  accept = 'image/*',
  maxSize = 5,
  title = 'Profile Picture',
  description = 'Drag and drop an image here, or click to select',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback((file: File): boolean => {
    setError(null);

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return false;
    }

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB`);
      return false;
    }

    return true;
  }, [maxSize]);

  const handleFileSelect = useCallback((file: File) => {
    if (validateFile(file)) {
      onFileSelect(file);
    }
  }, [validateFile, onFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);



  const handleClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        handleFileSelect(target.files[0]);
      }
    };
    input.click();
  }, [accept, handleFileSelect]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {title}
      </Typography>
      
      <Paper
        sx={{
          p: 3,
          border: 2,
          borderColor: isDragOver ? 'primary.main' : 'divider',
          borderStyle: 'dashed',
          borderRadius: 2,
          backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {currentImageUrl ? (
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={currentImageUrl}
                sx={{ width: 120, height: 120, fontSize: 48 }}
              />
              {onFileRemove && (
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'error.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'error.dark',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFileRemove();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              {isLoading ? (
                <CircularProgress size={60} />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
              )}
              <Typography variant="body1" color="text.secondary" textAlign="center">
                {description}
              </Typography>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Max size: {maxSize}MB
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}; 